// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search as SearchIcon, Filter, ExternalLink, Target, Sparkles, SearchX } from 'lucide-react';
import ApiService from '../services/api';
import { useAppContext } from '../context/AppContext';
import { cn } from '../lib/cn';
import { TwCard } from './ui/TwCard';
import {
	initEdgeEmbeddings,
	embedQueryInBrowser,
	embedTextsInBrowser,
	cosineSimilarity,
} from '../utils/edgeAI';

function useDebouncedValue(value, delay) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);
	return debounced;
}

const Recherche = () => {
	const fieldBase =
		'rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-primary-900 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500';
	const labelCls = 'mb-1 block text-sm font-medium text-neutral-600';

	const { books } = useAppContext();
	const [q, setQ] = useState('');
	const [mode, setMode] = useState('hybrid');
	const [k, setK] = useState(5);
	const [author, setAuthor] = useState('');
	const [series, setSeries] = useState('');
	const [language, setLanguage] = useState('');
	const [tags, setTags] = useState('');
	
	// P1 - Filtres avancés
	const [yearFrom, setYearFrom] = useState('');
	const [yearTo, setYearTo] = useState('');
	const [difficulty, setDifficulty] = useState('');
	const [docType, setDocType] = useState('');
	const [publisher, setPublisher] = useState('');
	const [keywords, setKeywords] = useState('');
	const [showAdvanced, setShowAdvanced] = useState(false);
	
	const [results, setResults] = useState([]);
	/**
	 * Les deux endpoints ne renvoient pas la même chose :
	 * - /api/search       → des PASSAGES (score, extrait, chunk_idx) ;
	 * - /api/search/advanced → des LIVRES (métadonnées, ni score ni extrait).
	 * Les afficher avec le même gabarit produisait « Score : undefined » et un
	 * lien vers le lecteur pointant sur un chunk inexistant.
	 */
	const [resultKind, setResultKind] = useState('passage');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	// Distingue "l'utilisateur n'a pas encore lancé de recherche" vs "aucun résultat trouvé"
	const [hasSearched, setHasSearched] = useState(false);

	const debouncedQ = useDebouncedValue(q, 150);
	const titleEmbeddingsRef = useRef(new Map());
	const [titleEmbedsReady, setTitleEmbedsReady] = useState(false);
	const [bookEmbedCount, setBookEmbedCount] = useState(0);
	const [semanticBusy, setSemanticBusy] = useState(false);
	const [localSuggestions, setLocalSuggestions] = useState([]);
	/** Progression 0–1 du premier téléchargement du modèle WASM (IndexedDB). */
	const [edgeEmbedProgress, setEdgeEmbedProgress] = useState(null);

	const booksFingerprint = useMemo(
		() =>
			(books || [])
				.map((b) => `${b.id}:${String(b.title || b.name || '')}`)
				.sort()
				.join('|'),
		[books],
	);

	useEffect(() => {
		let cancelled = false;
		const list = Array.isArray(books) ? books : [];
		if (!list.length) {
			titleEmbeddingsRef.current = new Map();
			setBookEmbedCount(0);
			setTitleEmbedsReady(false);
			return undefined;
		}
		(async () => {
			try {
				await initEdgeEmbeddings((info) => {
					if (!cancelled) setEdgeEmbedProgress(info);
				});
				if (cancelled) return;
				setEdgeEmbedProgress(null);
				const titles = list.map((b) => String(b.title || b.name || '').trim() || ' ');
				const ids = list.map((b) => String(b.id));
				const vecs = await embedTextsInBrowser(titles);
				if (cancelled) return;
				const m = new Map();
				ids.forEach((id, i) => {
					if (vecs[i] && vecs[i].length) m.set(id, vecs[i]);
				});
				titleEmbeddingsRef.current = m;
				setBookEmbedCount(m.size);
				setTitleEmbedsReady(true);
			} catch (e) {
				if (!cancelled) {
					titleEmbeddingsRef.current = new Map();
					setBookEmbedCount(0);
					setTitleEmbedsReady(false);
					setEdgeEmbedProgress(null);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [booksFingerprint, books]);

	useEffect(() => {
		let cancelled = false;
		const query = String(debouncedQ || '').trim();
		if (!query || !titleEmbedsReady || titleEmbeddingsRef.current.size === 0) {
			setLocalSuggestions([]);
			return undefined;
		}
		(async () => {
			setSemanticBusy(true);
			try {
				const qv = await embedQueryInBrowser(query);
				if (cancelled || !qv || !qv.length) {
					setLocalSuggestions([]);
					return;
				}
				const scored = [];
				titleEmbeddingsRef.current.forEach((vec, bookId) => {
					const sim = cosineSimilarity(qv, vec);
					const book = (books || []).find((b) => String(b.id) === bookId);
					if (book) {
						scored.push({
							bookId,
							title: book.title || book.name || bookId,
							author: book.author,
							sim,
						});
					}
				});
				scored.sort((a, b) => b.sim - a.sim);
				setLocalSuggestions(scored.slice(0, 8));
			} catch {
				setLocalSuggestions([]);
			} finally {
				if (!cancelled) setSemanticBusy(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [debouncedQ, titleEmbedsReady, books]);

	const openBookFromLibrary = (bookId) => {
		window.open(`/lecteur?book=${encodeURIComponent(bookId)}`, '_blank');
	};

	/** Au moins un filtre renseigné : la recherche avancée peut alors se passer
	 *  d'un terme de requête, ce que l'endpoint accepte. */
	const filtresActifs = [
		author, series, language, tags,
		yearFrom, yearTo, difficulty, docType, publisher, keywords,
	].some((v) => v && String(v).trim());

	const onSearch = async (e) => {
		if (e) e.preventDefault();
		setLoading(true);
		setError('');
		setHasSearched(true);
		try {
			const useAdvanced = showAdvanced || filtresActifs;
			if (useAdvanced) {
				const params = { query: q, search_type: mode, per_page: 20 };
				if (author.trim()) params.author = author.trim();
				if (series.trim()) params.series = series.trim();
				if (language.trim()) params.language = language.trim();
				if (tags.trim()) params.tags = tags.trim();
				if (yearFrom.trim()) params.year_from = parseInt(yearFrom, 10);
				if (yearTo.trim()) params.year_to = parseInt(yearTo, 10);
				if (difficulty) params.difficulty = difficulty;
				if (docType) params.format = docType;
				if (publisher.trim()) params.publisher = publisher.trim();
				if (keywords.trim()) params.keywords = keywords.trim();
				const data = await ApiService.searchAdvanced(params);
				setResultKind('livre');
				setResults(Array.isArray(data.results) ? data.results : []);
			} else {
				const data = await ApiService.search(q, { mode, k });
				// GET /api/search renvoie un TABLEAU NU : lire `data.results`
				// laissait la liste systématiquement vide.
				setResultKind('passage');
				setResults(Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []));
			}
		} catch (err) {
			setError(err?.message || 'Erreur de recherche.');
		} finally {
			setLoading(false);
		}
	};

	const openInReader = (hit) => {
		const bookId = hit.book_id;
		const chunkIdx = hit.chunk_idx;
		const cs = hit.char_start || 0;
		const ce = hit.char_end || 0;
		const ps = hit.page_start || hit.page_number || '';
		const pe = hit.page_end || '';
		const url = `/lecteur?book=${bookId}&chunk=${chunkIdx}` + (cs ? `&cs=${cs}&ce=${ce}` : '') + (ps ? `&ps=${ps}&pe=${pe || ps}` : '');
		window.open(url, '_blank');
	};

	return (
		<div className="recherche-page mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6">
			<h1 className="flex items-center gap-2 text-2xl font-semibold text-primary-900">
				<SearchIcon size={24} aria-hidden /> Recherche
			</h1>
			<form
				onSubmit={onSearch}
				className="recherche-form recherche-form-grid grid grid-cols-1 gap-3 md:grid-cols-3"
			>
				<input
					type="text"
					placeholder="Requête..."
					value={q}
					onChange={(e) => setQ(e.target.value)}
					className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-primary-900 shadow-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
				/>
				<select
					value={mode}
					onChange={(e) => setMode(e.target.value)}
					className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
				>
					<option value="hybrid">Hybride</option>
					<option value="vector">Vectorielle</option>
					<option value="fts">Lexicale</option>
				</select>
				<button
					type="submit"
					// Une recherche par filtres seuls est légitime : exiger un terme
					// de requête rendait tout le panneau de filtres inutilisable.
					disabled={(!q.trim() && !filtresActifs) || loading}
					className="rounded-md bg-primary-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{loading ? 'Recherche...' : 'Lancer'}
				</button>
				<div className="col-span-full space-y-2">
					<div className="mb-1.5 flex flex-wrap items-center gap-2 text-sm text-neutral-500">
						<Sparkles size={16} className="shrink-0 text-accent-500" aria-hidden />
						<span>Suggestions locales (embeddings navigateur, sans requête réseau)</span>
						{semanticBusy && <span className="text-xs">…</span>}
						{titleEmbedsReady && (
							<span className="text-xs text-neutral-400">({bookEmbedCount} titres indexés)</span>
						)}
					</div>
					{edgeEmbedProgress != null && (edgeEmbedProgress.progress == null || edgeEmbedProgress.progress < 1) && (
						<div className="mb-2 max-w-md">
							<div className="mb-1 text-xs text-neutral-400">
								Chargement du modèle local (première visite, ~22 Mo mis en cache IndexedDB)
								{edgeEmbedProgress.file ? ` — ${edgeEmbedProgress.file}` : ''}
							</div>
							<div className="h-1 overflow-hidden rounded-full bg-neutral-200">
								<div
									className="h-full bg-indigo-500 transition-[width] duration-150 ease-out"
									style={{
										width: `${Math.min(100, Math.round((Number(edgeEmbedProgress.progress) || 0) * 100))}%`,
									}}
								/>
							</div>
						</div>
					)}
					{localSuggestions.length > 0 && (
						<ul className="flex max-h-52 flex-col gap-1 overflow-auto rounded-lg border border-border bg-surface-subtle p-1">
							{localSuggestions.map((s) => (
								<li key={s.bookId}>
									<button
										type="button"
										onClick={() => openBookFromLibrary(s.bookId)}
										className="w-full rounded-md px-2.5 py-2 text-left text-sm text-primary-900 transition hover:bg-white"
									>
										<strong>{s.title}</strong>
										{s.author ? ` — ${s.author}` : ''}
										<span className="ml-2 text-xs text-neutral-500">{(s.sim * 100).toFixed(1)}%</span>
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
				{/* Filtres de base */}
				<div className="col-span-full flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
					<Filter size={16} className="hidden text-neutral-500 sm:block" aria-hidden />
					<input
						placeholder="Auteur"
						value={author}
						onChange={(e) => setAuthor(e.target.value)}
						className={cn(fieldBase, 'min-w-[120px] flex-1')}
					/>
					<input
						placeholder="Série"
						value={series}
						onChange={(e) => setSeries(e.target.value)}
						className={cn(fieldBase, 'min-w-[100px] flex-1')}
					/>
					<input
						placeholder="Langue (fr|en...)"
						value={language}
						onChange={(e) => setLanguage(e.target.value)}
						className={cn(fieldBase, 'min-w-[100px] flex-1')}
					/>
					<input
						placeholder="Tags (séparés par ,)"
						value={tags}
						onChange={(e) => setTags(e.target.value)}
						className={cn(fieldBase, 'min-w-[160px] flex-[2]')}
					/>
					<label className="flex items-center gap-2 text-sm text-neutral-600">
						<span className="tabular-nums">k</span>
						<input
							type="range"
							min="1"
							max="20"
							value={k}
							onChange={(e) => setK(parseInt(e.target.value, 10))}
							className="accent-accent-500"
						/>
						<span className="w-6 tabular-nums">{k}</span>
					</label>
					<button
						type="button"
						onClick={() => setShowAdvanced(!showAdvanced)}
						className={cn(
							'rounded-md px-3 py-2 text-sm font-medium transition',
							showAdvanced ? 'bg-accent-500 text-white hover:bg-accent-600' : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'
						)}
					>
						{showAdvanced ? 'Masquer' : 'Avancé'}
					</button>
				</div>

				{/* Filtres avancés */}
				{showAdvanced && (
					<TwCard className="col-span-full recherche-filters-grid grid grid-cols-1 gap-3 bg-surface-subtle p-4 sm:grid-cols-2 lg:grid-cols-3">
						<h3 className="col-span-full m-0 text-base font-semibold text-neutral-800">Filtres avancés</h3>

						<div>
							<label className={labelCls}>Période</label>
							<div className="flex gap-1">
								<input
									type="number"
									placeholder="De (année)"
									value={yearFrom}
									onChange={(e) => setYearFrom(e.target.value)}
									className={cn(fieldBase, 'min-w-0 flex-1')}
								/>
								<input
									type="number"
									placeholder="À (année)"
									value={yearTo}
									onChange={(e) => setYearTo(e.target.value)}
									className={cn(fieldBase, 'min-w-0 flex-1')}
								/>
							</div>
						</div>

						{/* Correctif audit P3 : champs BISAC/Dewey retirés — l'endpoint
						    advanced_search n'accepte pas ces filtres (ils étaient sans effet). */}

						<div>
							<label className={labelCls}>Difficulté</label>
							<select
								value={difficulty}
								onChange={(e) => setDifficulty(e.target.value)}
								className={cn(fieldBase, 'w-full')}
							>
								<option value="">Toutes</option>
								<option value="beginner">Débutant</option>
								<option value="intermediate">Intermédiaire</option>
								<option value="advanced">Avancé</option>
							</select>
						</div>

						<div>
							<label className={labelCls}>Type</label>
							<select value={docType} onChange={(e) => setDocType(e.target.value)} className={cn(fieldBase, 'w-full')}>
								<option value="">Tous types</option>
								<option value="academic">Académique</option>
								<option value="manual">Manuel</option>
								<option value="technical">Technique</option>
								<option value="fiction">Fiction</option>
								<option value="report">Rapport</option>
							</select>
						</div>

						<div>
							<label className={labelCls}>Éditeur</label>
							<input
								placeholder="Nom de l'éditeur"
								value={publisher}
								onChange={(e) => setPublisher(e.target.value)}
								className={cn(fieldBase, 'w-full')}
							/>
						</div>

						<div>
							<label className={labelCls}>Mots-clés</label>
							<input
								placeholder="Mots-clés (séparés par ,)"
								value={keywords}
								onChange={(e) => setKeywords(e.target.value)}
								className={cn(fieldBase, 'w-full')}
							/>
						</div>

						<div className="flex items-end sm:col-span-2 lg:col-span-1">
							<button
								type="button"
								onClick={() => {
									setYearFrom('');
									setYearTo('');
									// setBisac / setDewey ont été appelés ici alors que les états
									// correspondants avaient été retirés : le clic levait un
									// ReferenceError et n'effaçait donc AUCUN filtre.
									setDifficulty('');
									setDocType('');
									setPublisher('');
									setKeywords('');
									setAuthor('');
									setSeries('');
									setLanguage('');
									setTags('');
								}}
								className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
							>
								Effacer filtres
							</button>
						</div>
					</TwCard>
				)}
			</form>

			{error && (
				<div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
					{error}
				</div>
			)}

			<div className="mt-4 grid gap-3">
				{/* Loading skeleton — feedback visuel pendant la recherche RAG (AUD-008) */}
				{loading &&
					Array.from({ length: 3 }).map((_, i) => (
						<TwCard key={`skeleton-${i}`} className="p-4">
							<div className="flex items-center justify-between gap-2">
								<div className="h-4 w-24 animate-pulse rounded bg-neutral-200" />
								<div className="h-7 w-40 animate-pulse rounded bg-neutral-200" />
							</div>
							<div className="mt-3 space-y-2">
								<div className="h-4 w-2/3 animate-pulse rounded bg-neutral-200" />
								<div className="h-3 w-full animate-pulse rounded bg-neutral-100" />
								<div className="h-3 w-11/12 animate-pulse rounded bg-neutral-100" />
								<div className="h-3 w-3/4 animate-pulse rounded bg-neutral-100" />
							</div>
						</TwCard>
					))}

				{/* Empty state : aucun résultat après une recherche effective */}
				{!loading && !error && hasSearched && results.length === 0 && (
					<div
						role="status"
						className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-neutral-300 bg-surface-subtle p-10 text-center"
					>
						<SearchX size={36} className="text-neutral-400" aria-hidden />
						<h2 className="m-0 text-base font-semibold text-primary-900">
							Aucun résultat pour « {q.trim()} »
						</h2>
						<p className="m-0 max-w-md text-sm text-neutral-500">
							Essayez d'élargir votre requête, de changer le mode (
							<strong>Hybride</strong> donne souvent les meilleurs résultats) ou de
							retirer un filtre.
						</p>
					</div>
				)}

				{/* Rappel du type de résultats affichés */}
				{!loading && !error && results.length > 0 && (
					<p className="m-0 text-sm text-neutral-500" data-testid="resume-resultats">
						{resultKind === 'passage'
							? `${results.length} passage${results.length > 1 ? 's' : ''} trouvé${results.length > 1 ? 's' : ''}`
							: `${results.length} livre${results.length > 1 ? 's' : ''} correspondant${results.length > 1 ? 's' : ''} aux filtres`}
					</p>
				)}

				{/* Résultats : passages (recherche plein texte / sémantique) */}
				{!loading &&
					resultKind === 'passage' &&
					results.map((hit, i) => (
						<TwCard key={hit.book_id ? `${hit.book_id}-${hit.chunk_idx}` : i} className="p-4" data-testid="resultat-passage">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<div className="flex items-center gap-2 text-sm">
									<Target size={16} className="text-accent-500" aria-hidden />
									<strong>Score :</strong>
									<span className="tabular-nums">
										{typeof hit.score === 'number' ? hit.score.toFixed(3) : '—'}
									</span>
								</div>
								<button
									type="button"
									onClick={() => openInReader(hit)}
									className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-subtle px-2 py-1 text-sm font-medium text-primary-900 transition hover:bg-neutral-100"
								>
									<ExternalLink size={16} aria-hidden /> Ouvrir dans le lecteur
								</button>
							</div>
							<div className="mt-3 text-neutral-800">
								<div className="font-semibold">
									{hit.title || 'Titre inconnu'}
									{hit.author ? ` — ${hit.author}` : ''}
								</div>
								<div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-neutral-600">
									{(hit.text || hit.content || '').slice(0, 800)}
								</div>
							</div>
						</TwCard>
					))}

				{/* Résultats : livres (recherche avancée par filtres) */}
				{!loading &&
					resultKind === 'livre' &&
					results.map((livre, i) => (
						<TwCard key={livre.book_id || i} className="p-4" data-testid="resultat-livre">
							<div className="flex flex-wrap items-start justify-between gap-2">
								<div className="min-w-0">
									<div className="font-semibold text-primary-900">
										{livre.title || livre.file_original_name || 'Titre inconnu'}
									</div>
									{livre.author && (
										<div className="mt-0.5 text-sm text-neutral-600">{livre.author}</div>
									)}
									<div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
										{livre.series && <span>Série : {livre.series}</span>}
										{livre.year && <span>{livre.year}</span>}
										{livre.publisher && <span>{livre.publisher}</span>}
										{livre.lang && <span>{String(livre.lang).toUpperCase()}</span>}
									</div>
								</div>
								<button
									type="button"
									onClick={() => openBookFromLibrary(livre.book_id)}
									className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-surface-subtle px-2 py-1 text-sm font-medium text-primary-900 transition hover:bg-neutral-100"
								>
									<ExternalLink size={16} aria-hidden /> Ouvrir le livre
								</button>
							</div>
							{livre.description && (
								<p className="mt-3 line-clamp-3 text-sm leading-relaxed text-neutral-600">
									{String(livre.description).slice(0, 400)}
								</p>
							)}
						</TwCard>
					))}
			</div>
		</div>
	);
};

export default Recherche;


